// Example TypeScript types for the Rent vs Buy Calculator API
// These would be auto-generated from the Python models

export enum LoanType {
  FHA = "fha",
  CONVENTIONAL = "conventional"
}

export enum PropertyTaxStrategy {
  REASSESS = "reassess",
  CAPPED = "capped"
}

export enum PMIRemovalStrategy {
  SCHEDULED = "scheduled",
  APPRAISAL = "appraisal"
}

export interface RentalStrategy {
  /** Monthly rental income */
  monthly_income?: number;
  /** Vacancy factor (0.0-1.0) */
  occupancy_rate?: number;
  /** How long this income lasts */
  duration_years?: number;
  /** Optional override for rental income inflation (0 = use market assumptions) */
  rent_increase_rate?: number;
}

export interface MarketAssumptions {
  /** Annual property appreciation rate */
  property_appreciation_rate?: number;
  /** Annual investment return rate */
  investment_return_rate?: number;
  /** Annual rent increase rate */
  rent_increase_rate?: number;
}

export interface LoanDetails {
  /** Type of mortgage loan */
  loan_type?: LoanType;
  /** Annual mortgage interest rate */
  mortgage_interest_rate?: number;
  /** Loan term in years */
  loan_term_years?: number;
  /** Down payment as percentage of home price */
  down_payment_percent?: number;
  /** Annual PMI rate */
  pmi_rate?: number;
  /** LTV threshold for PMI removal */
  pmi_removal_threshold?: number;
  /** FHA upfront mortgage insurance premium rate */
  ufmip_rate?: number;
  /** Whether FHA upfront premium is financed */
  finance_ufmip?: boolean;
  /** Strategy controlling how PMI is removed */
  pmi_removal_strategy?: PMIRemovalStrategy;
  /** Year when appraisal occurs for PMI removal (appraisal strategy) */
  appraisal_year?: number | null;
  /** Optional LTV override to assume after appraisal */
  appraisal_ltv_override?: number | null;
  /** FHA annual mortgage insurance premium rate */
  fha_mip_rate?: number;
  /** Years after which FHA MIP can be removed (for 10%+ down payment) */
  fha_mip_removal_years?: number;
  /** FHA minimum down payment percentage */
  fha_min_down_payment_percent?: number;
  /** FHA down payment threshold for MIP removal eligibility */
  fha_high_down_payment_threshold?: number;
  /** Conventional loan down payment threshold below which PMI is required */
  conventional_pmi_threshold?: number;
  /** FHA loan limit for base areas */
  fha_loan_limit_base?: number;
  /** FHA loan limit for high-cost areas */
  fha_loan_limit_high_cost?: number;
}

export interface PropertyDetails {
  /** Property purchase price */
  home_price: number;
  /** Annual property tax rate */
  property_tax_rate?: number;
  /** Monthly HOA fees */
  hoa_monthly?: number;
  /** Monthly insurance cost */
  insurance_monthly?: number;
  /** Annual maintenance as % of home value */
  maintenance_rate?: number;
}

export interface TransactionCosts {
  /** Realtor fees for buying (as %) */
  realtor_fees_percent?: number;
  /** Whether buyer pays their agent fee at purchase */
  buyer_pays_realtor_fee?: boolean;
  /** Listing agent fee percentage at purchase */
  seller_agent_fee_percent?: number;
  /** Whether buyer covers listing agent fee at purchase */
  buyer_pays_seller_agent_fee?: boolean;
  /** Buying closing costs */
  closing_costs?: number;
  /** Seller credits that offset buyer cash to close */
  seller_credit?: number;
  /** Realtor fees for selling (as %) */
  selling_realtor_fees_percent?: number;
  /** Selling closing costs */
  selling_closing_costs?: number;
}

export interface AnalysisSettings {
  /** Analysis period in years */
  time_horizon_years?: number;
  /** Property tax growth strategy */
  property_tax_strategy?: PropertyTaxStrategy;
  /** Annual cap when using capped strategy */
  property_tax_cap_rate?: number;
}

export interface TaxSettings {
  /** Enable simplified tax modeling */
  enabled?: boolean;
  /** Marginal rate for rental income */
  rental_income_tax_rate?: number;
  /** Rate applied to realized investment gains */
  investment_tax_rate?: number;
  /** Rate applied to deductions (mortgage interest, property tax) */
  marginal_income_tax_rate?: number;
  /** Maximum deductible property tax */
  salt_deduction_cap?: number;
  /** Capital gains tax rate */
  capital_gains_rate?: number;
  /** Primary home capital gains exclusion */
  primary_home_exclusion?: number;
  /** Standard deduction used when itemizing */
  standard_deduction?: number;
}

export interface AncillaryCostSettings {
  /** Enable ancillary cash-flow modeling */
  enabled?: boolean;
  /** Security deposit in months of rent */
  renting_security_deposit_months?: number;
  /** Broker fee as percent of first-year rent */
  renting_broker_fee_percent?: number;
  /** Monthly renters insurance cost */
  renters_insurance_monthly?: number;
  /** Portion of deposit refunded at move-out */
  deposit_refund_rate?: number;
  /** Annual reserve for renovations/CapEx */
  buying_capex_reserve_annual?: number;
  /** Lender credit applied at closing */
  lender_credit?: number;
  /** Discount points purchased at closing */
  rate_buydown_points?: number;
  /** Interest rate reduction per discount point */
  rate_buydown_rate_per_point?: number;
}

export interface RentingParams {
  /** Monthly rent with roommates (0 = not applicable) */
  current_rent_with_roommates?: number;
  /** Monthly rent living alone */
  current_rent_living_alone?: number;
  /** Year when you change living situation (0 = never) */
  move_out_year?: number;
  /** Additional rental income while renting */
  rental_strategy?: RentalStrategy;
}

export interface BuyingParams {
  /** How you'll monetize the owned property */
  rental_strategy?: RentalStrategy;
}

export interface CalculatorParams {
  property_details: PropertyDetails;
  loan_details?: LoanDetails;
  transaction_costs?: TransactionCosts;
  market_assumptions?: MarketAssumptions;
  analysis_settings?: AnalysisSettings;
  tax_settings?: TaxSettings;
  ancillary_cost_settings?: AncillaryCostSettings;
  renting_params?: RentingParams;
  buying_params?: BuyingParams;
  scenario_type?: string; // 'buying' or 'renting' - used for baseline comparisons
}

export interface AnalysisResponse {
  /** Year when buying becomes better than renting */
  break_even_year?: number | null;
  /** Net worth with buying scenario */
  final_buying_net_worth: number;
  /** Ending invested surplus balance for the buying scenario */
  final_buying_surplus_balance: number;
  /** Net worth with renting scenario */
  final_renting_net_worth: number;
  /** Difference (buying - renting) */
  net_worth_difference: number;
  /** Total upfront costs for buying */
  upfront_costs: number;
  /** Breakdown of how the cash to close is allocated */
  upfront_cost_breakdown: Record<string, number>;
  /** Total cash spent on buying over analysis period */
  total_buying_cash_outflow: number;
  /** Total rent paid over analysis period */
  total_rent_paid: number;
  /** Property tax strategy used */
  property_tax_strategy: PropertyTaxStrategy;
  /** Annual cap applied when strategy is capped */
  property_tax_cap_rate: number;
  /** Net tax impact of the buying scenario (positive = cost) */
  total_buying_taxes: number;
  /** Net tax impact of the renting scenario (positive = cost) */
  total_renting_taxes: number;
  /** Total ancillary costs for the buying scenario */
  total_buying_ancillary_costs: number;
  /** Total ancillary costs for the renting scenario */
  total_renting_ancillary_costs: number;
  /** Estimated capital gains tax due upon sale */
  capital_gains_tax: number;
  /** PMI removal assumption applied in the analysis */
  pmi_removal_strategy?: PMIRemovalStrategy | null;
  /** Year when PMI ends under the selected assumption */
  pmi_removal_year?: number | null;
  /** Year-by-year breakdown (optional) */
  yearly_data?: YearlyData | null;
}

export interface YearlyData {
  years: number[];
  buying_net_worth: number[];
  buying_equity_only?: number[];
  renting_net_worth: number[];
  net_worth_difference: number[];
  annual_rents: number[];
  home_values: number[];
  monthly_costs: number[];
  rental_income: number[];
  buying_surplus_investment_values?: number[];
  buying_surplus_monthly_contributions?: number[];
  buying_surplus_tax_cash_flows?: number[];
  property_tax_assessed_values: number[];
  total_cash_outflows: number[];
  total_rent_paid: number[];
  property_tax_paid: number[];
  mortgage_interest_paid: number[];
  buying_tax_cash_flows: number[];
  renting_tax_cash_flows: number[];
  buying_ancillary_cash_flows: number[];
  renting_ancillary_cash_flows: number[];
}

export interface ErrorResponse {
  /** Error message */
  error: string;
  /** Additional error details */
  details?: Record<string, any> | null;
}

export interface ScenarioInfo {
  /** Scenario name */
  name: string;
  /** Scenario description */
  description: string;
  /** Scenario parameters */
  parameters: CalculatorParams;
}

export interface ValidationResponse {
  valid: boolean;
  warnings?: string[];
  errors?: string[];
}

// API Client helper types
export interface ApiResponse<T> {
  data?: T;
  error?: ErrorResponse;
}

export interface AnalyzeRequest {
  params: CalculatorParams;
  include_yearly_data?: boolean;
}
